import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CKEditorComponent from '../../components/CKEditorComponent';
import TextInput from '../../components/TextInput';
import TextAreaInput from '../../components/TextAreaInput';
import MultiSelect from '../../components/MultiSelect';
import FileUpload from '../../components/FileUpload';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import blogApi from '../../api/blogApi';
import blogCategoryApi from '../../api/blogCategoryApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const EditBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    description: '',
    categories: [],
    image: undefined,
    is_active: 0,
  });
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [blogFound, setBlogFound] = useState(true);

  useEffect(() => {
    const fetchBlogData = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const blogResponse = await blogApi.getBlogByIdentifier(id);
        if (!blogResponse.success || !blogResponse.data) {
          setBlogFound(false);
          setGeneralError(blogResponse.message || 'Blog not found.');
          setLoading(false);
          return;
        }
        const blogToEdit = blogResponse.data;

        const categoriesResponse = await blogApi.getCategoriesForBlog(id);
        let blogCategories = [];
        if (categoriesResponse.success && categoriesResponse.data) {
          blogCategories = categoriesResponse.data.map(cat => cat.id);
        } else {
          addToast('Failed to load categories for this blog.', 'error');
        }

        setFormData({
          title: blogToEdit.title,
          excerpt: blogToEdit.excerpt,
          description: blogToEdit.description,
          categories: blogCategories,
          image: undefined,
          is_active: blogToEdit.is_active ? 1 : 0,
        });
        setCurrentImageUrl(blogToEdit.image);
        setBlogFound(true);

        const allCategoriesResponse = await blogCategoryApi.getBlogCategories({ paginate: false, includeTrashed: false });
        if (allCategoriesResponse.success && allCategoriesResponse.data) {
          setAvailableCategories(allCategoriesResponse.data.map(cat => ({ value: cat.id, label: cat.name })));
        } else {
          addToast('Failed to load available categories for selection.', 'error');
        }

      } catch (err) {
        console.error('Failed to fetch blog or categories:', err);
        setBlogFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load blog. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : (type === 'file' ? files[0] : value),
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData({ ...formData, description: content });
    if (validationErrors.description) {
      setValidationErrors(prev => ({ ...prev, description: undefined }));
    }
  };

  const handleCategoryChange = (selectedOptions) => {
    setFormData({ ...formData, categories: selectedOptions ? selectedOptions.map(option => option.value) : [] });
    if (validationErrors.categories) {
      setValidationErrors(prev => ({ ...prev, categories: undefined }));
    }
  };

  const handleImageRemove = () => {
    setFormData({ ...formData, image: null });
    setCurrentImageUrl(null);
    if (validationErrors.image) {
      setValidationErrors(prev => ({ ...prev, image: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      if (key === 'categories') {
        formData[key].forEach(catId => data.append('categories[]', catId));
      } else if (key === 'image') {
        if (formData[key] instanceof File) {
          data.append(key, formData[key]);
        } else if (formData[key] === null) {
          data.append(key, '');
        }
      } else {
        data.append(key, formData[key]);
      }
    }
    data.append('_method', 'PUT');

    try {
      await blogApi.updateBlog(id, data);
      addToast('Blog updated successfully!', 'success');
      navigate(ROUTES.BLOGS);
    } catch (err) {
      console.error('Failed to update blog:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update blog. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!blogFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Blog Not Found</h1>
        <p className="text-text-gray">{generalError || 'The blog you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate(ROUTES.BLOGS)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Blogs List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Blog</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Blog Title"
          name="title"
          id="title"
          value={formData.title}
          onChange={handleChange}
          required={true}
          placeholder="Blog Title"
          error={validationErrors?.title?.[0]}
        />
        <TextAreaInput
          label="Excerpt"
          name="excerpt"
          id="excerpt"
          value={formData.excerpt}
          onChange={handleChange}
          required={true}
          rows={3}
          placeholder="Excerpt"
          error={validationErrors?.excerpt?.[0]}
        />
        
        <CKEditorComponent
          label="Description"
          value={formData.description}
          onChange={handleDescriptionChange}
          required={true}
          error={validationErrors?.description?.[0]}
        />

        <MultiSelect
          label="Blog Categories"
          options={availableCategories}
          value={formData.categories.map(catId => ({ value: catId, label: availableCategories.find(c => c.value === catId)?.label || catId }))}
          onChange={handleCategoryChange}
          placeholder="Select categories..."
          error={validationErrors?.categories?.[0]}
          required={true}
        />
        <FileUpload
          label="Blog Image"
          name="image"
          id="image"
          onChange={handleChange}
          error={validationErrors?.image?.[0]}
          required={false} // Image is now optional
          currentFileUrl={currentImageUrl}
          onRemoveFile={handleImageRemove}
          fileType="image"
          fileName="Image"
        />
        <CheckboxInput
          label="Active"
          name="is_active"
          id="is_active"
          checked={formData.is_active}
          onChange={handleChange}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Blog
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.BLOGS)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditBlog;